import './PillSelect.scss'
import { useId, useRef, type ComponentProps } from 'react'
import { MdOutlineExpandMore } from 'react-icons/md'
import { clx } from '../clx'
import { useSelectListbox } from '../SelectListbox'

export interface PillSelectOption {
  value: string
  label: string
}

export interface PillSelectProps extends Omit<
  ComponentProps<'button'>,
  'value' | 'onChange' | 'children' | 'type' | 'role'
> {
  value: string
  options: readonly PillSelectOption[]
  onChange: (value: string) => void
}

/**
 * A compact pill that opens a listbox popover (ADR 0021): the WAI-ARIA
 * select-only combobox. Focus stays on the pill; the active option is
 * `aria-activedescendant`; the listbox is the shared `useSelectListbox`.
 * For short, fixed option lists (rows per page); use Select or Combobox in
 * forms.
 */
export function PillSelect({
  value,
  options,
  onChange,
  id,
  className,
  disabled,
  ref,
  ...rest
}: PillSelectProps) {
  const generated = useId()
  const buttonId = id ?? `pill-select-${generated}`
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const listbox = useSelectListbox({
    anchorRef: buttonRef,
    id: buttonId,
    items: options,
    value,
    onPick: onChange,
    disabled
  })
  const selected = options.find((option) => option.value === value)

  return (
    <>
      <button
        {...rest}
        ref={(node) => {
          buttonRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        id={buttonId}
        type="button"
        role="combobox"
        {...listbox.triggerProps}
        disabled={disabled}
        className={clx('pill-select', className)}
      >
        <span className="pill-select__value">{selected?.label ?? value}</span>
        <span className="pill-select__chevron" aria-hidden="true">
          <MdOutlineExpandMore />
        </span>
      </button>
      {listbox.popup}
    </>
  )
}
